import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface ProcessingContextType {
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
}

export const ProcessingContext = createContext<
  ProcessingContextType | undefined
>(undefined);

export const useProcessingContext = () => {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error(
      'useProcessingContext must be used within a ProcessingProvider',
    );
  }
  return context;
};

export const ProcessingProvider = ({ children }: { children: ReactNode }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname();

  // Reset processing state when pathname changes
  useEffect(() => {
    setIsProcessing(false);
  }, [pathname]);

  const value = useMemo(
    () => ({ isProcessing, setIsProcessing }),
    [isProcessing],
  );

  return (
    <ProcessingContext.Provider value={value}>
      {children}
    </ProcessingContext.Provider>
  );
};
